<template>
  <div>
    <DialogBase
      v-model="inputValue"
      :title="
        $t('common.input_supplier-name') +
        (editMode ? $t('common.common_edit') : $t('common.common_register'))
      "
      :width="width"
    >
      <div class="px-5 pb-5" v-if="Object.keys(inputData).length > 0">
        <InputFormNew
          v-model="inputData"
          :detail="items"
          :titleCols="inputFormStyle.titleCols"
          :contentsCols="inputFormStyle.contentsCols"
          :master-data="suppliers"
        >
          <template #postalCode="{ item }">
            <!-- 郵便番号 -->
            <PostalCodeArea
              v-model="item.postalCode"
              :height="inputFormStyle.inputItemHeight"
              :countryId="item.country.value"
              @click="getAddressFromPostalCode"
            />
          </template>
          <template #suggestedReceivingLocation="{ item }">
            <!-- 希望入庫場所 -->
            <SelectSuggestedReceivingLocation
              v-model="item.suggestedReceivingLocationId"
              :height="inputFormStyle.inputItemHeight"
              :returnObject="false"
            />
          </template>
        </InputFormNew>
        <NormalButton
          :text="buttonName"
          textSize="16px"
          height="50px"
          width="100%"
          class="pt-5"
          @click.native="complete()"
        />
      </div>
    </DialogBase>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import DialogBase from '@/components/layouts/Dialogs/DialogBase'
import {
  InputFormNew,
  PostalCodeArea,
  SelectSuggestedReceivingLocation,
  NormalButton
} from '@/components/items'
import { countryCallingCodeUtil, countryUtil } from '@/util'

export default {
  components: {
    DialogBase,
    InputFormNew,
    PostalCodeArea,
    SelectSuggestedReceivingLocation,
    NormalButton
  },
  props: {
    value: { default: false },
    width: { default: '540px' },
    buttonName: { default: 'common.input_save' },
    supplierData: {
      type: Object,
      require: false,
      default: () => ({})
    },
    editMode: {
      type: Boolean,
      require: false,
      default: false
    }
  },
  data: () => ({
    inputFormStyle: {
      titleCols: 3,
      contentsCols: 9,
      inputItemHeight: '32px'
    },
    suggestedReceivingLocationList: [],
    suppliers: [],
    inputData: {},
    items: [
      {
        name: 'name',
        text: 'order.order_supplier-name',
        required: true
      },
      {
        name: 'abbreviation',
        text: 'order.order_abbreviation',
        required: true
      },
      {
        name: 'description',
        text: 'common.input_supplier-assistance-explanation'
      },
      {
        name: 'section',
        text: 'order.order_department'
      },
      {
        name: 'rep',
        text: 'order.order_rep'
      },
      {
        name: 'phone',
        text: 'common.input_phone-number',
        component: 'PhoneNumberInput'
      },
      {
        name: 'fax',
        text: 'common.input_fax-number',
        component: 'PhoneNumberInput'
      },
      {
        name: 'email',
        text: 'common.input_email'
      },
      {
        name: 'country',
        text: 'common.input_country',
        cols: Math.ceil(5),
        component: 'SelectCountry'
      },
      {
        name: 'postalCode',
        text: 'common.input_postal-code',
        cols: Math.ceil(5)
      },
      {
        name: 'region',
        text: 'common.input_region',
        cols: Math.ceil(5),
        component: 'SelectRegion'
      },
      {
        name: 'address1',
        text: 'common.input_address_1',
        placeholder: 'common.input_municipality'
      },
      {
        name: 'address2',
        text: 'common.input_address_2',
        placeholder: 'common.input_after-house-number'
      },
      {
        name: 'suggestedReceivingLocation',
        text: 'order.order_suggested-receiving-location'
      },
      {
        name: 'paymentCriteria',
        text: 'common.input_payment-criteria'
      },
      {
        name: 'sortNumber',
        text: 'common.input_sort-number',
        required: true,
        placeholder: '',
        cols: Math.ceil(5),
        component: 'SelectSortNumber'
      },
      {
        name: 'isEnabled',
        text: 'product.product_display-setting',
        component: 'CheckEnabled'
      }
    ]
  }),
  // async created() {
  //   await this.init()
  // },

  methods: {
    ...mapActions('Supplier', [
      'registerSupplier',
      'editSupplier',
      'getSuggestedReceivingLocationListFromApi'
    ]),
    ...mapGetters('Supplier', [
      'getSuppliersFromState',
      'getSupplierMaxSortNumber',
      'getSuggestedReceivingLocationListFromState'
    ]),
    ...mapGetters('Common', [
      'getCountryCallingCodeJpFromState',
      'getCountryJpFromState'
    ]),

    setCountryCallingCode(countryId) {
      return this.editMode
        ? countryCallingCodeUtil.getPhoneNumberInfoById(countryId)
        : this.getCountryCallingCodeJpFromState()
    },
    setCountryId(countryId) {
      return this.editMode
        ? countryUtil.getCountryIdInfo(countryId)
        : this.getCountryJpFromState()
    },
    async init() {
      await this.getSuggestedReceivingLocationList()

      const formatData = { ...this.supplierData }
      // 新規登録の際に「表示する」項目のダイアログ初期値をtrueで設定
      if (!Object.keys(formatData).length) {
        formatData.isEnabled = true
      }
      formatData.phone = {
        countryCode: this.setCountryCallingCode(
          this.supplierData.phoneNumberCountryId
        ),
        phoneNumber: this.supplierData.phoneNumber
      }
      formatData.fax = {
        countryCode: this.setCountryCallingCode(
          this.supplierData.faxNumberCountryId
        ),
        phoneNumber: this.supplierData.faxNumber
      }
      formatData.country = this.setCountryId(this.supplierData.addressCountryId)
      formatData.region = countryUtil.getRegionInfo(
        this.supplierData.addressCountryId,
        this.supplierData.regionId
      )
      formatData.sortNumber = this.editMode
        ? this.supplierData.sortNumber
        : this.getSupplierMaxSortNumber() + 1

      this.inputData = formatData
      this.suppliers = this.getSuppliersFromState()
    },
    getAddressFromPostalCode(...args) {
      const [address] = args

      this.inputData.region = countryUtil.getRegionInfoByName(
        this.inputData.country.value,
        address.region
      )
      this.inputData.address1 = address.locality
      this.inputData.address2 = address.street
    },
    async getSuggestedReceivingLocationList() {
      await this.getSuggestedReceivingLocationListFromApi()
      this.suggestedReceivingLocationList = this.createSelectBoxValueFrom(
        this.getSuggestedReceivingLocationListFromState()
      )
    },
    createSelectBoxValueFrom(suggestedReceivingLocationList) {
      return suggestedReceivingLocationList.map((ele) => {
        return {
          value: ele.suggestedReceivingLocationId,
          label: ele.location
        }
      })
    },
    async complete() {
      const success = this.editMode
        ? await this.editSupplier({
            supplierId: this.inputData.supplierId,
            params: this.inputData
          })
        : await this.registerSupplier(this.inputData)

      if (success) {
        this.$emit('success')
      }
    }
  },
  computed: {
    inputValue: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    }
  },
  watch: {
    inputValue: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal) this.init()
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.baseStyle {
  font-size: 14px;
}
</style>
